import type { ChecklistDocument, DocumentState, EstateRow } from '@acasa/shared'
import { EstateBriefings } from '@/features/briefings/estate-briefings'
import { EstateImageStrip } from './estate-images'
import { useEstateDetail } from './use-checklists'

const GROUPS: { state: DocumentState; title: string; dot: string }[] = [
  { state: 'open', title: 'Offen', dot: 'bg-line-strong' },
  { state: 'not_applicable', title: 'Nicht erforderlich', dot: 'bg-na' },
  { state: 'done', title: 'Vorhanden', dot: 'bg-done' },
]

function formatDate(iso: string | null | undefined) {
  return iso ? new Date(iso).toLocaleDateString('de-DE') : null
}

function Meta({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-faint text-[10px] tracking-wider uppercase">{label}</dt>
      <dd className="text-ink mt-0.5 text-xs">{value}</dd>
    </div>
  )
}

export function EstateDetailPanel({
  estate,
  documents,
}: {
  estate: EstateRow
  documents: ChecklistDocument[]
}) {
  const { data, isPending } = useEstateDetail(estate.estateId)

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      {/* Object facts */}
      <div className="lg:border-line lg:border-r lg:pr-6">
        <h3 className="text-ink text-sm font-medium">{estate.title ?? 'Ohne Titel'}</h3>
        <p className="text-muted mt-0.5 font-mono text-[11px]">
          {estate.externalNumber ?? `#${estate.estateId}`} · onOffice-ID {estate.estateId}
        </p>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
          <Meta label="Vermarktung" value={estate.marketingType} />
          <Meta label="Status" value={estate.status} />
          <Meta label="Aktiv" value={estate.activeState} />
          <Meta label="Betreuer" value={estate.agent} />
          <Meta label="Angelegt" value={formatDate(estate.createdAt)} />
          <Meta label="Geändert" value={formatDate(estate.modifiedAt)} />
          <Meta label="Verkauft" value={formatDate(estate.soldAt)} />
        </dl>

        {isPending && <p className="text-faint mt-4 text-xs">Details werden geladen …</p>}

        {data && <EstateImageStrip images={data.images} estateId={estate.estateId} />}

        <EstateBriefings estate={estate} />

        {data?.groups.map((group) => (
          <div key={group.title} className="border-line mt-4 border-t pt-3">
            <p className="text-faint mb-1.5 text-[10px] tracking-wider uppercase">{group.title}</p>
            <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1">
              {group.entries.map((entry) => (
                <div key={entry.label} className="contents">
                  <dt className="text-muted truncate text-xs">{entry.label}</dt>
                  <dd className="text-ink tnum text-right font-mono text-xs">{entry.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}

        {data && data.groups.length === 0 && !isPending && (
          <p className="text-faint mt-4 text-xs">
            Keine weiteren Objektdaten in onOffice gepflegt.
          </p>
        )}
      </div>

      {/* Document checklist */}
      <div className="grid gap-x-8 gap-y-4 sm:grid-cols-3">
        {GROUPS.map((group) => {
          const items = documents.filter(
            (document) => (estate.steps[document.key]?.state ?? 'open') === group.state,
          )
          if (items.length === 0) return null

          return (
            <div key={group.state}>
              <p className="text-faint mb-1.5 text-[10px] tracking-wider uppercase">
                {group.title} · {items.length}
              </p>
              <ul className="space-y-1">
                {items.map((document) => {
                  const step = estate.steps[document.key]
                  return (
                    <li key={document.key} className="flex items-start gap-2 text-xs">
                      <span
                        aria-hidden
                        className={`mt-1 size-1.5 shrink-0 rounded-full ${group.dot}`}
                      />
                      <span className="min-w-0">
                        <span className={group.state === 'open' ? 'text-ink' : 'text-muted'}>
                          {document.label}
                        </span>
                        {step?.note && (
                          <span className="text-brand ml-1.5 italic">{step.note}</span>
                        )}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </div>
    </div>
  )
}
